import { useEffect, useState } from 'react';

import { GetRetailRateRequestsResponse } from '@classpass/protobuf-definitions/lib/web/grpc/partners/retail_rate_requests';

import URLS from 'constants/urls';
import { httpClient } from 'helpers/http';
import { reportError } from 'helpers/error-logging';

const createdAtMs = request => request.created_at?.getTime() ?? 0;

// The venue endpoint returns every request for the service; the partner's
// current status is the most recently created one.
const mostRecent = requests =>
  requests.reduce(
    (latest, request) => (!latest || createdAtMs(request) > createdAtMs(latest) ? request : latest),
    null
  );

const fetchManualRetailRateRequest = async (venueId, msiUuid) => {
  const response = await httpClient.get(URLS.manageServices.services.retailRateRequestsByVenue(venueId, { msiUuid }));
  const { retailRateRequest = [] } = GetRetailRateRequestsResponse.fromJSON(response.data);
  // Re-filter client-side: if the endpoint returns other services' requests
  // despite the msi_uuid param, the newest one venue-wide would mask this
  // service's own status (e.g. a rejected request rendered as pending).
  const requestsForService = retailRateRequest.filter(request => request.msi_service_uuid === msiUuid);
  return mostRecent(requestsForService);
};

// The manual edit page has no per-service request on its services payload, so it
// fetches the current retail-rate-request for the service's msi_service_uuid.
// Mirrors useIntegratedService (numeric-enum fromJSON shape). Returns null while
// loading, when there's no uuid, or on error.
export default function useManualRetailRateRequest(venueId, msiUuid) {
  const [retailRateRequest, setRetailRateRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!venueId || !msiUuid) {
      setRetailRateRequest(null);
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);

    const load = async () => {
      try {
        const result = await fetchManualRetailRateRequest(venueId, msiUuid);
        if (isMounted) setRetailRateRequest(result);
      } catch (error) {
        reportError('Failed to fetch manual retail rate request', { error, venueId, msiUuid });
        if (isMounted) setRetailRateRequest(null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [venueId, msiUuid]);

  return { retailRateRequest, isLoading };
}
